import { useState } from 'react';
import { CheckCircle2, XCircle, Loader2, Play, RefreshCw, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useEnrollmentChecklist } from '../hooks/useReports';
import QueryInfoButton from '../components/QueryInfoButton';
import { ENROLLMENT_CHECK_QUERIES } from '../constants/queries';

const CHECKLIST_ITEMS = [
  {
    id: 'kp-cohort-assignments',
    name: 'Enrollment Cohort Assignments for KP',
    description: 'Verify all consumers in KP tenant are assigned to cohorts 139 or 140',
    passCondition: 'No consumers found without cohort assignment (count = 0)',
    failCondition: 'Consumers exist without proper cohort assignments',
  },
  // Add more checklist items here as needed
];

function ChecklistItem({ item, result, isRunning, onRun }) {
  const [expanded, setExpanded] = useState(false);

  const getStatusIcon = () => {
    if (isRunning) {
      return <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />;
    }
    if (!result) {
      return <div className="w-6 h-6 rounded-full border-2 border-gray-300" />;
    }
    if (result.passed) {
      return <CheckCircle2 className="w-6 h-6 text-green-500" />;
    }
    return <XCircle className="w-6 h-6 text-red-500" />;
  };

  const getStatusBg = () => {
    if (!result) return 'bg-white';
    if (result.passed) return 'bg-green-50 border-green-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className={`border rounded-lg ${getStatusBg()} transition-colors`}>
      <div className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            {getStatusIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{item.name}</h3>
              <div className="flex items-center gap-2">
                {result && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    result.passed
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {result.passed ? 'PASS' : 'FAIL'}
                  </span>
                )}
                <button
                  onClick={() => onRun(item.id)}
                  disabled={isRunning}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Run check"
                >
                  {isRunning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 text-gray-600" />
                  )}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-1">{item.description}</p>

            {result && (
              <div className="mt-3">
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  {expanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  View Details
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {expanded && result && (
        <div className="border-t px-4 py-3 bg-gray-50">
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Result Summary</span>
              <p className="text-sm text-gray-700 mt-1">
                {result.passed ? item.passCondition : item.failCondition}
              </p>
            </div>

            {result.summary && (
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase">Details</span>
                <p className="text-sm text-gray-700 mt-1">{result.summary}</p>
              </div>
            )}

            {result.data && result.data.length > 0 && (
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase">Query Results</span>
                <div className="mt-2 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        {Object.keys(result.data[0]).map((key) => (
                          <th
                            key={key}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                          >
                            {key.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {result.data.map((row, idx) => (
                        <tr key={idx}>
                          {Object.values(row).map((value, vidx) => (
                            <td key={vidx} className="px-3 py-2 whitespace-nowrap">
                              {value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.executedAt && (
              <div className="text-xs text-gray-400">
                Executed at: {new Date(result.executedAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EnrollmentChecklist() {
  const [results, setResults] = useState({});
  const [runningItems, setRunningItems] = useState(new Set());
  const { mutate, isPending } = useEnrollmentChecklist();

  const runCheck = (itemId) => {
    setRunningItems((prev) => new Set([...prev, itemId]));

    mutate(
      { checkId: itemId },
      {
        onSuccess: (response) => {
          setResults((prev) => ({
            ...prev,
            [itemId]: {
              passed: response.passed,
              data: response.data,
              summary: response.summary,
              executedAt: new Date().toISOString(),
            },
          }));
          setRunningItems((prev) => {
            const next = new Set(prev);
            next.delete(itemId);
            return next;
          });
        },
        onError: (error) => {
          setResults((prev) => ({
            ...prev,
            [itemId]: {
              passed: false,
              summary: `Error: ${error.message}`,
              executedAt: new Date().toISOString(),
            },
          }));
          setRunningItems((prev) => {
            const next = new Set(prev);
            next.delete(itemId);
            return next;
          });
        },
      }
    );
  };

  const runAllChecks = () => {
    CHECKLIST_ITEMS.forEach((item) => {
      runCheck(item.id);
    });
  };

  const passedCount = Object.values(results).filter((r) => r.passed).length;
  const failedCount = Object.values(results).filter((r) => !r.passed).length;
  const totalRun = passedCount + failedCount;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Enrollment Checklist</h2>
          <p className="text-gray-500 mt-1">
            Verify enrollment data integrity across systems
          </p>
        </div>
        <div className="flex items-center gap-4">
          <QueryInfoButton
            title="Enrollment Check Queries"
            queries={[ENROLLMENT_CHECK_QUERIES.kpCohortAssignments]}
          />
          <button
            onClick={runAllChecks}
            disabled={runningItems.size > 0}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${runningItems.size > 0 ? 'animate-spin' : ''}`} />
            Run All Checks
          </button>
        </div>
      </div>

      {totalRun > 0 && (
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-gray-700">{passedCount} Passed</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm font-medium text-gray-700">{failedCount} Failed</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              {CHECKLIST_ITEMS.length - totalRun} Not Run
            </span>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {CHECKLIST_ITEMS.map((item) => (
          <ChecklistItem
            key={item.id}
            item={item}
            result={results[item.id]}
            isRunning={runningItems.has(item.id)}
            onRun={runCheck}
          />
        ))}
      </div>
    </div>
  );
}
